import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookMarked, Calculator, BookOpen, Languages, Leaf, ArrowRight } from "lucide-react";
import DiagramRenderer from "@/components/DiagramRenderer";
import TellMeMoreButton from "@/components/TellMeMoreButton";
import MomentBanner from "@/components/MomentBanner";
import { toBullets } from "@/lib/textBullets";

// Subject → accent icon for recap cards.
const SUBJECT_ICON = {
  "Mathematics": Calculator,
  "English":     BookOpen,
  "Hindi":       Languages,
  "EVS":         Leaf,
};
const iconFor = (subject) => SUBJECT_ICON[subject] || BookOpen;

/**
 * Derive an ordered list of step strings for any recap payload.
 *  - Prefer `items` if present (already split by the import normaliser).
 *  - Otherwise parse `body`/`content` via the shared bullet parser.
 *  - Final fallback: a single-step list containing just the lead text.
 */
function deriveSteps(payload) {
  if (Array.isArray(payload?.items) && payload.items.length > 0) {
    return payload.items.map((s) => String(s).trim()).filter(Boolean);
  }
  const parsed = toBullets(payload?.body || payload?.content || "");
  if (parsed.length > 0) return parsed;
  const lead = (payload?.content || "").trim();
  return lead ? [lead] : [];
}

/**
 * Shared tap-to-reveal flow used by every recap format.
 * Renders the first card immediately; each tap of "Next step" reveals the next.
 * Once all are revealed, the subject-tinted "Tell me more" pill appears.
 */
function RevealSteps({ steps, subject, recordId, firstIcon = true, renderStep }) {
  const [revealed, setRevealed] = useState(1);
  const total = steps.length;
  const allRevealed = revealed >= total;
  const SubIcon = iconFor(subject);

  return (
    <div className="max-w-2xl mx-auto space-y-3" data-testid="recap-reveal">
      {steps.slice(0, revealed).map((step, i) => (
        <Card
          key={i}
          className="rounded-2xl p-5 md:p-6 mit-shadow border-sky-100 bg-white fade-up"
          data-testid={`recap-step-${i}`}
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-2xl bg-sky-100 flex items-center justify-center flex-shrink-0">
              {i === 0 && firstIcon
                ? <SubIcon className="w-5 h-5 text-sky-600" data-testid="recap-step-icon" />
                : <span className="font-bold text-sky-700 text-base">{i + 1}</span>}
            </div>
            <div className="flex-1 text-slate-800 text-base md:text-lg leading-relaxed">
              {renderStep ? renderStep(step, i) : step}
            </div>
          </div>
        </Card>
      ))}

      {!allRevealed && total > 1 && (
        <div className="text-center pt-2">
          <Button
            onClick={() => setRevealed((n) => Math.min(n + 1, total))}
            variant="outline"
            className="rounded-full border-2 border-sky-300 text-sky-700 hover:bg-sky-50 font-semibold tap-44 px-5 py-2"
            data-testid="recap-reveal-next-btn"
          >
            Next step <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <p className="text-xs text-slate-400 mt-2" data-testid="recap-reveal-progress">{revealed} of {total}</p>
        </div>
      )}

      {allRevealed && recordId && (
        <div className="pt-2 flex justify-center">
          <TellMeMoreButton scope="recap_summary" recordId={recordId} subject={subject} />
        </div>
      )}
    </div>
  );
}

export default function ReviseRecapRenderer({ payload, onProceed }) {
  const fmt = (payload?.format || "summary").toLowerCase();
  const moment = payload?.mitraa_moment;

  return (
    <div className="fade-up">
      {/* Gamification Moment Banner */}
      <MomentBanner moment={moment} className="mb-4" />
      
      <div className="text-center mb-6">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold uppercase tracking-wider" data-testid="recap-tag">
          <BookMarked className="w-3.5 h-3.5" /> Recap • {payload?.subject}
        </span>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-800 mt-3" data-testid="recap-title">
          {payload?.title || "Quick Recap"}
        </h2>
      </div>

      {payload?.has_diagram && payload?.record_id && (
        <div className="max-w-2xl mx-auto">
          <DiagramRenderer recordId={payload.record_id} description={payload.diagram_description} />
        </div>
      )}

      {fmt === "flashcard"
        ? <FlashcardCard payload={payload} />
        : <RevealSteps steps={deriveSteps(payload)} subject={payload?.subject} recordId={payload?.record_id} />
      }

      <div className="text-center mt-8">
        <Button
          onClick={onProceed}
          className="rounded-full bg-sky-400 hover:bg-sky-500 text-white text-base font-semibold px-8 py-6 mit-shadow tap-44"
          data-testid="recap-ready-btn"
        >
          I'm ready <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// Flashcard format keeps its tap-to-flip interaction; it's the only format that
// does NOT use the step-reveal flow (no items to reveal — front + back only).
function FlashcardCard({ payload }) {
  const [flipped, setFlipped] = useState(false);
  const front = payload?.content || payload?.title;
  const back = (payload?.items && payload.items[0]) || payload?.body;
  return (
    <div className="flip-perspective max-w-2xl mx-auto" data-testid="recap-flashcard">
      <div
        className={`flip-card relative w-full h-64 md:h-80 cursor-pointer no-select ${flipped ? "flipped" : ""}`}
        onClick={() => setFlipped(!flipped)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setFlipped(!flipped)}
        data-testid="flashcard-tap"
      >
        <Card className="flip-face absolute inset-0 rounded-3xl p-8 mit-shadow border-sky-100 bg-white flex flex-col items-center justify-center text-center">
          <span className="text-xs uppercase font-semibold text-sky-500 tracking-wider mb-3">Front — tap to flip</span>
          <p className="font-display text-2xl md:text-3xl text-slate-800">{front}</p>
        </Card>
        <Card className="flip-face flip-back absolute inset-0 rounded-3xl p-8 mit-shadow border-emerald-100 bg-emerald-50 flex flex-col items-center justify-center text-center">
          <span className="text-xs uppercase font-semibold text-emerald-600 tracking-wider mb-3">Back</span>
          <p className="font-display text-2xl md:text-3xl text-slate-800">{back}</p>
        </Card>
      </div>
      {payload?.record_id && (
        <div className="pt-4 flex justify-center">
          <TellMeMoreButton scope="recap_summary" recordId={payload.record_id} subject={payload.subject} />
        </div>
      )}
    </div>
  );
}
